import authService from './authService';
import {
  GoodsPickupOrder,
  GoodsPickupOrderFormData,
  GoodsPickupOrderSearchModel,
} from '../types/goodsPickupOrder';
import { PaginatedResponse, ApiResponse } from '../types/common';

class GoodsPickupOrderService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── List ───────────────────────────────────────────────────

  async getOrders(searchModel: GoodsPickupOrderSearchModel): Promise<PaginatedResponse<GoodsPickupOrder>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.is_delivered !== undefined && { is_delivered: searchModel.is_delivered.toString() }),
      ...(searchModel.status && { status: searchModel.status }),
      ...(searchModel.provider_id !== undefined && { provider_id: searchModel.provider_id.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-pickup-orders?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar ordens de retirada');

    const json = await response.json();
    return {
      success: json.success,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? searchModel.pageSize,
        total: json.data?.pagination?.total_items ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? 1,
      },
    };
  }

  // ─── Get by ID ──────────────────────────────────────────────

  async getOrderById(id: number): Promise<GoodsPickupOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-pickup-orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de retirada não encontrada');
      throw new Error('Falha ao buscar ordem de retirada');
    }
    const data: ApiResponse<GoodsPickupOrder> = await response.json();
    return data.data!;
  }

  // ─── Create ─────────────────────────────────────────────────

  async createOrder(formData: GoodsPickupOrderFormData): Promise<GoodsPickupOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/goods-pickup-orders', formData);
    if (!response.ok) throw new Error('Falha ao criar ordem de retirada');
    const data: ApiResponse<GoodsPickupOrder> = await response.json();
    return data.data!;
  }

  // ─── Update ─────────────────────────────────────────────────

  async updateOrder(id: number, formData: Partial<GoodsPickupOrderFormData>): Promise<GoodsPickupOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/goods-pickup-orders/${id}`, formData);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de retirada não encontrada');
      throw new Error('Falha ao atualizar ordem de retirada');
    }
    const data: ApiResponse<GoodsPickupOrder> = await response.json();
    return data.data!;
  }

  // ─── Delete ─────────────────────────────────────────────────

  async deleteOrder(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/goods-pickup-orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de retirada não encontrada');
      throw new Error('Falha ao excluir ordem de retirada');
    }
  }

  // ─── Mark as delivered ──────────────────────────────────────

  async markAsDelivered(id: number): Promise<GoodsPickupOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/goods-pickup-orders/${id}/deliver`, {});
    if (!response.ok) throw new Error('Falha ao marcar ordem como entregue');
    const data: ApiResponse<GoodsPickupOrder> = await response.json();
    return data.data!;
  }

  // ─── Cancel ─────────────────────────────────────────────────

  /**
   * Cancela a ordem (motivo obrigatório, máx. 500). O backend remove a parada da
   * ordem de qualquer rota em que ela ainda não foi retirada. 409 = já entregue/cancelada.
   */
  async cancelOrder(id: number, reason: string): Promise<GoodsPickupOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/goods-pickup-orders/${id}/cancel`, { reason });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      if (response.status === 404) throw new Error('Ordem de retirada não encontrada');
      if (response.status === 409) throw new Error(err.message || 'A ordem já foi entregue ou cancelada');
      throw new Error(err.message || 'Falha ao cancelar ordem de retirada');
    }
    const data: ApiResponse<GoodsPickupOrder> = await response.json();
    return data.data!;
  }

  // ─── Count pending (for notification bell) ──────────────────

  async getPendingCount(): Promise<number> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/goods-pickup-orders/pending-count');
    if (!response.ok) return 0;
    const data: ApiResponse<{ count: number }> = await response.json();
    return data.data?.count ?? 0;
  }

  // ─── Get purchase orders by provider (open status) ──────────

  async getOpenPurchaseOrdersByProvider(providerId: number): Promise<any[]> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({
      provider_id: providerId.toString(),
      status: '0',
      limit: '100',
      page: '1',
    });
    const response = await apiClient.get(`/purchase-orders?${params}`);
    if (!response.ok) return [];
    const json = await response.json();
    return Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []);
  }

  // ─── Get purchase order items ───────────────────────────────

  async getPurchaseOrderItems(purchaseOrderId: number): Promise<any[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/purchase-orders/${purchaseOrderId}`);
    if (!response.ok) return [];
    const json = await response.json();
    return json.data?.items || [];
  }

  // ─── Get sales order by ID ─────────────────────────────────

  async getSalesOrderById(orderId: number): Promise<any> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/orders/${orderId}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Pedido de venda não encontrado');
      throw new Error('Falha ao buscar pedido de venda');
    }
    const json = await response.json();
    return json.data;
  }

  // ─── Search sales orders ────────────────────────────────────

  async searchSalesOrders(query: string): Promise<any[]> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({
      search: query,
      page: '1',
      limit: '20',
    });
    const response = await apiClient.get(`/orders?${params}`);
    if (!response.ok) return [];
    const json = await response.json();
    return Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []);
  }

  // ─── Search products ────────────────────────────────────────

  async searchProducts(query: string): Promise<any[]> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({ q: query });
    const response = await apiClient.get(`/products/search?${params}`);
    if (!response.ok) return [];
    const json = await response.json();
    return Array.isArray(json.data) ? json.data : (Array.isArray(json.data?.data) ? json.data.data : []);
  }
  // ─── PDF download ───────────────────────────────────────────

  async downloadPdf(id: number): Promise<Blob> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-pickup-orders/${id}/pdf`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message ?? 'Erro ao gerar PDF');
    }
    return response.blob();
  }
}

export const goodsPickupOrderService = new GoodsPickupOrderService();
export default goodsPickupOrderService;
